import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { UserService } from '../services/user.service';
import { HotelService } from '../services/hotel.service';
import { environment } from '../../../environments/environment';

/**
 * HTTP error interceptor that handles authentication errors and provides
 * graceful error handling without losing user's unsaved work.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const userService = inject(UserService);
  const hotelService = inject(HotelService);
  const router = inject(Router);
  const modalService = inject(NgbModal);

  // Skip error handling for external third-party URLs (e.g. Nominatim geocoding)
  if (req.url.includes('nominatim.openstreetmap.org')) {
    return next(req);
  }

  // Skip auth error handling for login/auth endpoints
  const isAuthEndpoint = req.url.includes('/oauth/token') ||
                         req.url.includes('/oauth/login') ||
                         req.url.includes('/oauth/verify-2fa') ||
                         req.url.includes('/forgot-password') ||
                         req.url.includes('/reset-password');

  // Hotel-resource endpoints can return 401 for two different reasons:
  // (a) The user's session has expired — must logout and redirect to login.
  // (b) The user has a valid session but lost access to a specific hotel —
  //     HotelSelectionUrlGuard handles this case by showing a "hotel not found"
  //     message and redirecting to hotel selection. We must NOT logout here or
  //     we'd wipe a perfectly valid token.
  // We distinguish (a) from (b) by checking whether the token is expired.
  const isHotelResourceEndpoint = req.url.includes('/hotels/information/');

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      const isSessionExpired = isHotelResourceEndpoint && userService.isTokenExpired();
      const shouldHandleLogout = err.status === 401 && !isAuthEndpoint && (!isHotelResourceEndpoint || isSessionExpired);

      if (shouldHandleLogout) {
        // Clear local auth state synchronously so the redirect to login is not blocked
        // by a stale token cookie. The backend token revocation is skipped here because
        // the server already rejected the request with 401 — the token is already invalid.
        userService.clearSessionLocally();

        // Navigate to login with return URL — but only if we're not already on the login page
        // (multiple 401s from parallel requests could fire this repeatedly)
        const currentUrl = router.url;
        // Skip redirect if already on a public account page (login, register, forgot-password, etc.)
        // — those pages don't require authentication and redirecting away would confuse the user.
        if (!currentUrl.startsWith('/account/')) {
          // Close any open modals before navigating away so they don't bleed into the login page
          modalService.dismissAll();

          // A hotel-scoped manager losing access to their currently-viewed
          // hotel (e.g. a Super Admin archived it mid-session, HD-1013)
          // surfaces as a 401 with this dedicated error_code. currentUrl in
          // that case points at a page that will just 401 again the moment
          // they log back in — attaching it as returnUrl would loop them
          // straight back into the same logout. Only carry a returnUrl for
          // an actual session-expiry logout, where the page is still valid.
          // Only trust error_code from the app's own API
          const isOwnApiRequest = req.url.startsWith(environment.api.url);
          const isHotelAccessDenied = isOwnApiRequest && err.error?.error_code === 'HOTEL_ACCESS_DENIED';
          const queryParams: Record<string, string> = {
            reason: isHotelAccessDenied ? 'hotel_access_denied' : 'session_expired'
          };
          if (isHotelAccessDenied) {
            // clearSessionLocally() already dropped lastHotelId/hc_selected_hotel_*
            // from localStorage, but this is an SPA navigation, not a full
            // reload — HotelService's in-memory selected hotel survives the
            // logout untouched. Left as-is, HotelSelectionGuard reads that
            // stale in-memory hotelId on the next /dashboard visit and
            // redirects straight back to the archived hotel's URL, 401ing
            // again and looping through another logout/login cycle even
            // with returnUrl gone. Clear it here so re-login lands on a
            // clean slate instead.
            hotelService.clearSelectedHotel();
            // Also evict the cache itself, not just the current selection —
            // otherwise a later visit to this same hotel (e.g. via the
            // switcher) would still replay its stale pre-archive snapshot
            // through HotelSelectionUrlGuard's cached fast path.
            hotelService.clearHotelCache();
          } else {
            queryParams['returnUrl'] = currentUrl;
          }

          router.navigate(['/account/login'], { queryParams });
        }
      }

      // Note: 403 errors are NOT globally redirected to /access-denied.
      // Route-level access is handled by AccessAreaGuard.
      // API-level 403s should be handled by individual components/services.

      // Extract meaningful error message
      const errorMessage = err.error?.message || err.error?.errors?.[0]?.message || err.statusText || 'An error occurred';

      return throwError(() => ({
        status: err.status,
        message: errorMessage,
        errors: err.error?.errors || [],
        originalError: err
      }));
    })
  );
};
